/**
 * API client functions for product catalog operations
 */

import { API_BASE_URL } from '../config/api';

export interface ProductCatalogItem {
  id: string;
  practice_id?: string | null;
  based_on_standard_id?: string | null;
  name: string;
  product_type: string;
  description?: string | null;
  sku?: string | null;
  manufacturer?: string | null;
  notes?: string | null;
  is_active: boolean;
  is_stock_tracked: boolean;
  is_standard: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCatalogCreate {
  name: string;
  product_type: string;
  description?: string | null;
  sku?: string | null;
  manufacturer?: string | null;
  notes?: string | null;
  is_stock_tracked?: boolean;
  display_order?: number;
  based_on_standard_id?: string;
}

export interface ProductCatalogUpdate {
  name?: string;
  product_type?: string;
  description?: string | null;
  sku?: string | null;
  manufacturer?: string | null;
  notes?: string | null;
  is_active?: boolean;
  is_stock_tracked?: boolean;
  display_order?: number;
}

export interface ProductReferencesResponse {
  has_references: boolean;
  reference_count: number;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

export async function getProductCatalogItems(
  practiceId: string,
  includeInactive: boolean = false,
  search?: string
): Promise<ProductCatalogItem[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');
  if (search) params.append('search', search);
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch products' }));
    throw new Error(error.detail || 'Failed to fetch products');
  }

  return response.json();
}

export async function getProductCatalogItem(
  practiceId: string,
  itemId: string
): Promise<ProductCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products/${itemId}`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch product' }));
    throw new Error(error.detail || 'Failed to fetch product');
  }

  return response.json();
}

export async function createProductCatalogItem(
  practiceId: string,
  data: ProductCatalogCreate
): Promise<ProductCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create product' }));
    throw new Error(error.detail || 'Failed to create product');
  }

  return response.json();
}

export async function updateProductCatalogItem(
  practiceId: string,
  itemId: string,
  data: ProductCatalogUpdate
): Promise<ProductCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products/${itemId}`,
    { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update product' }));
    throw new Error(error.detail || 'Failed to update product');
  }

  return response.json();
}

export async function deleteProductCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products/${itemId}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete product' }));
    throw new Error(error.detail || 'Failed to delete product');
  }
}

export async function getProductReferences(
  practiceId: string,
  itemId: string
): Promise<ProductReferencesResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products/${itemId}/references`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to check references' }));
    throw new Error(error.detail || 'Failed to check references');
  }

  return response.json();
}

export async function hardDeleteProductCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/products/${itemId}/hard`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete product' }));
    throw new Error(error.detail || 'Failed to delete product');
  }
}
